import React, { useState, useEffect, useCallback } from 'react';
import {
  Share2, Globe, Lock, Copy, Check, ExternalLink,
  X, Loader2, Eye, Award, Calendar, MessageCircle,
  Link2, AlertCircle
} from 'lucide-react';
import './ShareModal.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareModal = ({ crate, onClose, onUpdate }) => {
  const [shareData, setShareData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [isSavingBattleTested, setIsSavingBattleTested] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  
  // Battle tested state
  const [battleTested, setBattleTested] = useState(false);
  const [linkedEventId, setLinkedEventId] = useState(null);
  const [linkedEventName, setLinkedEventName] = useState('');
  const [showEventLink, setShowEventLink] = useState(false);

  const getAuthToken = useCallback(() => localStorage.getItem('beatseek_token'), []);

  // Fetch share data
  const fetchShareData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/share-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setShareData(data);
        setBattleTested(data.battle_tested || false);
        setLinkedEventId(data.linked_event_id || null);
        setLinkedEventName(data.linked_event_name || '');
      }
    } catch (err) {
      console.error('Error fetching share data:', err);
      setError('Failed to load sharing data');
    } finally {
      setIsLoading(false);
    }
  }, [crate.id, getAuthToken]);

  useEffect(() => {
    fetchShareData();
  }, [fetchShareData]);

  // Toggle public/private
  const togglePublic = async () => {
    setIsToggling(true);
    try {
      const token = getAuthToken();
      const newStatus = !shareData?.is_public;
      
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/toggle-public`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_public: newStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle public status');

      const data = await response.json();
      setShareData(prev => ({
        ...prev,
        is_public: data.is_public,
        share_id: data.share_id,
        share_url: data.share_url
      }));

      if (onUpdate) {
        onUpdate({ is_public: data.is_public, share_id: data.share_id });
      }
    } catch (err) {
      console.error('Error toggling public:', err);
      setError('Failed to update sharing status');
    } finally {
      setIsToggling(false);
    }
  };

  // Save battle tested status
  const saveBattleTested = async () => {
    setIsSavingBattleTested(true);
    try {
      const token = getAuthToken();
      
      const response = await fetch(`${BACKEND_URL}/api/crates/${crate.id}/battle-tested`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          battle_tested: battleTested,
          linked_event_id: linkedEventId,
          linked_event_name: linkedEventName || null
        })
      });

      if (!response.ok) throw new Error('Failed to update battle tested status');

      const data = await response.json();
      setShareData(prev => ({
        ...prev,
        battle_tested: data.battle_tested,
        linked_event_id: data.linked_event_id,
        linked_event_name: data.linked_event_name
      }));

      if (onUpdate) {
        onUpdate({
          battle_tested: data.battle_tested,
          linked_event_id: data.linked_event_id,
          linked_event_name: data.linked_event_name
        });
      }
    } catch (err) {
      console.error('Error saving battle tested:', err);
      setError('Failed to update battle tested status');
    } finally {
      setIsSavingBattleTested(false);
    }
  };

  // Copy share link
  const copyShareLink = async () => {
    if (!shareData?.share_url) return;
    
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${shareData.share_url}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Share to Discord
  const shareToDiscord = () => {
    if (!shareData?.discord_share_text) return;
    
    // Open Discord with pre-filled message (user's Discord integration)
    const discordText = encodeURIComponent(shareData.discord_share_text);
    window.open(`https://discord.com/channels/@me?message=${discordText}`, '_blank');
  };

  // Navigate to event
  const goToEvent = () => {
    if (linkedEventId) {
      // Navigate to gig calendar with event ID
      window.location.href = `/events?highlight=${linkedEventId}`;
    }
  };

  if (isLoading) {
    return (
      <div className="share-modal-overlay" onClick={onClose}>
        <div className="share-modal" onClick={(e) => e.stopPropagation()}>
          <div className="share-modal-loading">
            <Loader2 size={32} className="spinning" />
            <span>Loading share settings...</span>
          </div>
        </div>
      </div>
    );
  }

  const fullShareUrl = shareData?.share_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${shareData.share_url}`
    : null;

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="share-modal-header">
          <h3>Share & Community</h3>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="share-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Content */}
        <div className="share-modal-content">
          {/* Public Toggle Section */}
          <div className="share-section">
            <div className="section-header">
              <Share2 size={18} />
              <h4>Public Sharing</h4>
            </div>
            
            <div className="public-toggle-row">
              <div className="toggle-info">
                {shareData?.is_public ? (
                  <>
                    <Globe size={18} className="public-icon" />
                    <span>This crate is <strong>public</strong></span>
                  </>
                ) : (
                  <>
                    <Lock size={18} className="private-icon" />
                    <span>This crate is <strong>private</strong></span>
                  </>
                )}
              </div>
              <button 
                className={`toggle-btn ${shareData?.is_public ? 'public' : 'private'}`}
                onClick={togglePublic}
                disabled={isToggling}
                data-testid="toggle-public-btn"
              >
                {isToggling ? (
                  <Loader2 size={16} className="spinning" />
                ) : shareData?.is_public ? (
                  'Make Private'
                ) : (
                  'Make Public'
                )}
              </button>
            </div>

            {shareData?.is_public && fullShareUrl && (
              <div className="share-links">
                <div className="share-url-row">
                  <input 
                    type="text" 
                    value={fullShareUrl} 
                    readOnly 
                    className="share-url-input"
                  />
                  <button 
                    className="copy-btn"
                    onClick={copyShareLink}
                    title="Copy link"
                    data-testid="copy-share-link-btn"
                  >
                    {copied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                  <a 
                    href={fullShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="open-btn"
                    title="Open in new tab"
                  >
                    <ExternalLink size={18} />
                  </a>
                </div>

                <div className="share-buttons">
                  <button className="share-discord-btn" onClick={shareToDiscord} data-testid="share-discord-btn">
                    <MessageCircle size={16} />
                    Share to Discord
                  </button>
                </div>

                {shareData?.public_views > 0 && (
                  <div className="view-count">
                    <Eye size={14} />
                    <span>{shareData.public_views} views</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Battle Tested Section */}
          <div className="share-section">
            <div className="section-header">
              <Award size={18} />
              <h4>Battle Tested</h4>
            </div>
            
            <div className="battle-tested-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={battleTested}
                  onChange={(e) => {
                    setBattleTested(e.target.checked);
                    if (!e.target.checked) {
                      setLinkedEventId(null);
                      setLinkedEventName('');
                    }
                  }}
                />
                <span className="checkmark"></span>
                <span>I played this set live</span>
              </label>
            </div>

            {battleTested && (
              <div className="event-link-section">
                <div className="event-link-row">
                  <div className="form-group">
                    <label>Event Name (optional)</label>
                    <input
                      type="text"
                      value={linkedEventName}
                      onChange={(e) => setLinkedEventName(e.target.value)}
                      placeholder="e.g., Friday Night at Club XYZ"
                    />
                  </div>
                </div>

                {linkedEventId ? (
                  <button className="go-to-event-btn" onClick={goToEvent}>
                    <Calendar size={16} />
                    Take me to event
                    <ExternalLink size={14} />
                  </button>
                ) : (
                  <div className="no-event-alert">
                    <AlertCircle size={14} />
                    <span>No event linked - add this gig to your calendar to link it</span>
                  </div>
                )}

                <button 
                  className="save-battle-btn"
                  onClick={saveBattleTested}
                  disabled={isSavingBattleTested}
                  data-testid="save-battle-tested-btn"
                >
                  {isSavingBattleTested ? (
                    <>
                      <Loader2 size={16} className="spinning" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={16} />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}

            {!battleTested && shareData?.battle_tested && (
              <button 
                className="save-battle-btn secondary"
                onClick={saveBattleTested}
                disabled={isSavingBattleTested}
              >
                {isSavingBattleTested ? (
                  <Loader2 size={16} className="spinning" />
                ) : (
                  'Remove Battle Tested Badge'
                )}
              </button>
            )}
          </div>

          {/* Purchase Links Info */}
          <div className="share-section purchase-info">
            <div className="section-header">
              <Link2 size={18} />
              <h4>Purchase Links</h4>
            </div>
            <p className="info-text">
              When sharing publicly, viewers can see purchase links for tracks in your crate. 
              This helps other DJs find and buy the same tracks!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
